<template>
    <zen-slide-in
        :isOpen="isOpen"
        title="Update user"
        :error="error"
        @close="$emit('close')"
    >
        <template #body>
            <div class="space-y-1 mb-6">
                <p class="text-sm text-taupe-600">
                    Update user information, preferences, and permissions.
                </p>
            </div>

            <div class="space-y-5">
                <!-- Name fields grouped -->
                <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                        <label
                            for="update-first-name"
                            class="block text-sm font-medium leading-6 text-taupe-900"
                        >
                            First name
                            <span class="text-red-500">*</span>
                        </label>
                        <div class="mt-2">
                            <input
                                type="text"
                                name="update-first-name"
                                id="update-first-name"
                                :value="data.first_name"
                                @input="
                                    $emit('update:data', {
                                        ...data,
                                        first_name: $event.target.value,
                                    })
                                "
                                placeholder="John"
                                class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                            />
                        </div>
                    </div>
                    <div>
                        <label
                            for="update-last-name"
                            class="block text-sm font-medium leading-6 text-taupe-900"
                        >
                            Last name
                            <span class="text-red-500">*</span>
                        </label>
                        <div class="mt-2">
                            <input
                                type="text"
                                name="update-last-name"
                                id="update-last-name"
                                :value="data.last_name"
                                @input="
                                    $emit('update:data', {
                                        ...data,
                                        last_name: $event.target.value,
                                    })
                                "
                                placeholder="Smith"
                                class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                            />
                        </div>
                    </div>
                </div>

                <!-- Email -->
                <div>
                    <label
                        for="update-email"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Email address
                        <span class="text-red-500">*</span>
                    </label>
                    <div class="mt-2">
                        <input
                            type="email"
                            name="update-email"
                            id="update-email"
                            :value="data.email"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    email: $event.target.value,
                                })
                            "
                            placeholder="john.smith@example.com"
                            class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                        />
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        This email will be used for login and notifications.
                    </p>
                </div>

                <!-- Marketing emails -->
                <div class="relative flex gap-x-3 items-start pt-2 pb-1">
                    <div class="flex h-6 items-center">
                        <input
                            type="checkbox"
                            name="update-newsletter"
                            id="update-newsletter"
                            :checked="data.allow_marketing_emails"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    allow_marketing_emails:
                                        $event.target.checked,
                                })
                            "
                            class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600 transition-colors"
                        />
                    </div>
                    <div class="text-sm leading-6">
                        <label
                            for="update-newsletter"
                            class="font-medium text-taupe-900"
                            >Subscribe to newsletter</label
                        >
                        <p class="text-taupe-500 text-xs mt-0.5">
                            Send occasional updates and tips. User can
                            unsubscribe anytime.
                        </p>
                    </div>
                </div>

                <!-- Personal access tokens -->
                <div class="relative flex gap-x-3 items-start pt-2 pb-1">
                    <div class="flex h-6 items-center">
                        <input
                            type="checkbox"
                            name="update-personal-access-tokens-enabled"
                            id="update-personal-access-tokens-enabled"
                            :checked="data.personal_access_tokens_enabled"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    personal_access_tokens_enabled:
                                        $event.target.checked,
                                })
                            "
                            class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600 transition-colors"
                        />
                    </div>
                    <div class="text-sm leading-6">
                        <label
                            for="update-personal-access-tokens-enabled"
                            class="font-medium text-taupe-900"
                            >Personal access tokens enabled</label
                        >
                        <p class="text-taupe-500 text-xs mt-0.5">
                            When disabled, this user loses access to the
                            Personal Access Tokens screen and their existing
                            tokens immediately stop working. Turning it back on
                            restores them unchanged.
                        </p>
                    </div>
                </div>

                <!-- Date format -->
                <div>
                    <label
                        for="update-date-format"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Date format
                        <span class="text-red-500">*</span>
                    </label>
                    <div class="mt-2">
                        <select
                            name="update-date-format"
                            id="update-date-format"
                            :value="data.date_format"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    date_format: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                        >
                            <option disabled value="">
                                Select a date format
                            </option>
                            <option value="ddd MMM D, YYYY">
                                {{
                                    moment(Date.now()).format('ddd MMM D, YYYY')
                                }}
                            </option>
                            <option value="MMM D, YYYY">
                                {{ moment(Date.now()).format('MMM D, YYYY') }}
                            </option>
                            <option value="D MMM YYYY">
                                {{ moment(Date.now()).format('D MMM YYYY') }}
                            </option>
                            <option value="YYYY-MM-DD">
                                {{ moment(Date.now()).format('YYYY-MM-DD') }}
                            </option>
                        </select>
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        Choose how dates are displayed throughout the
                        application.
                    </p>
                </div>

                <!-- Time format -->
                <div>
                    <label
                        for="update-time-format"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Time format
                        <span class="text-red-500">*</span>
                    </label>
                    <div class="mt-2">
                        <select
                            name="update-time-format"
                            id="update-time-format"
                            :value="data.time_format"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    time_format: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                        >
                            <option disabled value="">
                                Select a time format
                            </option>
                            <option value="h:mm a">
                                {{ moment(Date.now()).format('h:mm a') }}
                            </option>
                            <option value="HH:mm:ss">
                                {{ moment(Date.now()).format('HH:mm:ss') }}
                            </option>
                        </select>
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        Choose how times are displayed throughout the
                        application.
                    </p>
                </div>
            </div>
        </template>
        <template v-slot:buttons>
            <zen-button
                variant="secondary"
                size="medium"
                @click="$emit('close')"
            >
                Cancel
            </zen-button>
            <zen-button
                variant="primary"
                size="medium"
                :processing="processing"
                :disabled="!canSubmit"
                @click="$emit('confirm')"
            >
                Update User
            </zen-button>
        </template>
    </zen-slide-in>
</template>

<script>
import moment from 'moment'

export default {
    name: 'UpdateUserSlideIn',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        error: {
            type: [String, Object],
            default: null,
        },
        canSubmit: {
            type: Boolean,
            default: false,
        },
        processing: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'confirm', 'update:data'],
    methods: {
        moment(datetime) {
            return moment(datetime)
        },
    },
}
</script>
